import {
  getShareUrl,
  getWishlistByShareHash,
  getWishlists,
} from "@app/scripts/api/wishlist"
import { selectDefaultWishlist } from "@app/scripts/utils"
import { IWishlist } from "@app/types/wishlist"

class WishlistProducts extends HTMLElement {
  owner: string | null = null
  wishlists: IWishlist[] | null = null
  popup: HTMLDivElement | null = null
  shareId: string | null = null
  shareUrl: string | null = null
  isShared: boolean = false

  constructor() {
    super()
    this.owner =
      window.ShopifyAnalytics.meta.page.customerId?.toString() || null
    this.popup = this.querySelector("generic-popup")

    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has("share_id")) {
      this.shareId = urlParams.get("share_id") || null
    }

    if (!this.owner && !this.shareId) {
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname)
    }
  }

  async connectedCallback() {
    if (this.shareId) {
      await this.getWishlistFromShareHash(this.shareId)
      this.prepareView()
      this.querySelector(".wishlist-products__action--share")?.remove()
      return
    }

    if (!this.owner) return
    await this.getWishlists()
    this.prepareView()
    this.querySelector(".wishlist-products__action--share")?.addEventListener(
      "click",
      this.handleShareClick
    )

    this.popup
      ?.querySelector(".wishlist-products__share__button--cancel")
      ?.addEventListener("click", () => {
        this.popup?.classList.remove("is-visible")
      })
    const copyButton = this.popup?.querySelector(
      ".wishlist-products__share__button--copy"
    )
    copyButton?.addEventListener("click", () => {
      copyButton!.textContent = "Copied!"
      if (this.shareUrl) navigator.clipboard.writeText(this.shareUrl)
    })
  }

  getWishlists = async () => {
    const res = await getWishlists(this.owner!)
    this.wishlists = res?.wishlists || null
    console.debug("[wishlist] Wishlists fetched. Wishlists:", this.wishlists)
  }

  getWishlistFromShareHash = async (shareHash: string) => {
    const res = await getWishlistByShareHash(encodeURIComponent(shareHash))
    this.wishlists = res ? [res] : null
    this.isShared = true
    console.debug("[wishlist] Wishlists fetched. Wishlists:", this.wishlists)
  }

  hasDefaultWishlist = () => {
    return selectDefaultWishlist(this.wishlists || [])
  }

  hasProductsInDefaultWishlist = () => {
    const wishlist = selectDefaultWishlist(this.wishlists || [])
    return !!wishlist?.items.item?.length
  }

  hasProductsInWishlist = () => {
    const wishlist = this.wishlists?.[0]
    return wishlist?.items.item && wishlist.items.item.length > 0
  }

  prepareView = () => {
    const emptyStateElement = document.querySelector(
      ".wishlist-products__empty"
    )
    const mainHeaderContainer = document.querySelector(
      ".wishlist-products__main .wishlist-products__main__header__text"
    )

    if (
      (this.isShared && this.hasProductsInWishlist()) ||
      (this.hasDefaultWishlist() && this.hasProductsInDefaultWishlist())
    ) {
      this.classList.add("is-not-empty")

      // SEO Studio hawk request: Only have one H1 present
      if (emptyStateElement) {
        emptyStateElement?.querySelector("h1")?.remove()
      }

      this.hideOtherSections()
    } else {
      this.classList.add("is-empty")

      // SEO Studio hawk request: Only have one H1 present
      if (mainHeaderContainer) {
        mainHeaderContainer?.querySelector("h1")?.remove()
      }

      this.showOtherSections()
    }
  }

  handleShareClick = async () => {
    const wishlist = selectDefaultWishlist(this.wishlists || [])
    if (!wishlist || !this.owner) return null

    this.querySelector(".wishlist-products__action--share")!.setAttribute(
      "disabled",
      "disabled"
    )
    const url = await getShareUrl(wishlist)
    this.shareUrl = url
    this.querySelector(".wishlist-products__action--share")!.removeAttribute(
      "disabled"
    )
    if (!this.popup) return
    this.popup.classList.add("is-visible")
    this.popup.querySelector("input")!.value = url
    this.popup.querySelector(
      ".wishlist-products__share__button--copy"
    )!.textContent = "Copy Link"
  }

  hideOtherSections() {
    const thisSection = this.parentElement
    thisSection?.parentElement
      ?.querySelectorAll(".shopify-section")
      ?.forEach((section) => {
        if (section.id !== thisSection.id) {
          section.classList.add("is-hidden")
        }
      })
  }

  showOtherSections() {
    const thisSection = this.parentElement
    thisSection?.parentElement
      ?.querySelectorAll(".shopify-section")
      ?.forEach((section) => {
        if (section.id !== thisSection.id) {
          section.classList.remove("is-hidden")
        }
      })
  }
}

customElements.define("wishlist-products", WishlistProducts)
